import { sections } from '../About/helpers';
import { isOntologyGraphFeatureVisible } from '@/config/ontologyGraph';
import { isGeoBrowseEnabled } from '@/config/geoBrowse';
import { isEventsEnabled } from '@/config/events';
import { isGseaEnabled } from '@/modules/enrichment/helpers';

export type NavMegaMenuId = 'docs' | 'tools' | 'insights';

export type DocsIconKey = 'book' | 'rocket' | 'chart' | 'database' | 'metrics' | 'document' | 'ontology';

export type InsightsIconKey = 'caseStudies' | 'publications' | 'events' | 'blog';

export type DocsMenuItem = {
  label: string;
  subtitle: string;
  href: string;
  icon: DocsIconKey;
};

export type DocsMenuColumn = {
  title: string;
  items: DocsMenuItem[];
};

export type ToolGroupId = 'analysis' | 'access' | 'integrations';

export type ToolFeatureBanner = {
  eyebrow: string;
  title: string;
  body: string;
  ctaLabel?: string;
};

export type NavToolMenuItem = {
  id: string;
  label: string;
  description: string;
  infoHref: string;
  toolHref: string;
  group: ToolGroupId;
  /** Short pill label shown on the Tools overview cards. */
  tag: string;
  /**
   * ISO date used to pick the featured banner tool among eligible tools.
   */
  releasedAt: string;
  /**
   * When false, the tool can appear in the grid (if its feature flag is on)
   * but must not win the featured banner. Use for WIP tools that are still
   * off in production — so local/test flags do not override the prod “latest”.
   * Flip to true when the tool ships to production.
   */
  canFeatureInProduction?: boolean;
  toolExternal?: boolean;
  showInfo?: boolean;
  showTool?: boolean;
  iconSrc?: string;
  iconWidth?: number;
  iconAlt?: string;
  roles?: string[];
  requiresOntologyGraph?: boolean;
  requiresGsea?: boolean;
  requiresGeoBrowse?: boolean;
  isPaidTool?: boolean;
  isNew?: boolean;
  feature?: ToolFeatureBanner;
};

export const TOOL_GROUPS: { id: ToolGroupId; label: string }[] = [
  { id: 'analysis', label: 'Analysis' },
  { id: 'access', label: 'Access' },
  { id: 'integrations', label: 'Integrations' },
];

export const TOOLS_DOCS_HREF = '/Tools?section=console';

export function toolShowsInfoLink(tool: NavToolMenuItem): boolean {
  if (tool.showInfo === false) return false;
  if (tool.showInfo === true) return true;
  return tool.infoHref !== tool.toolHref;
}

export function isNavToolFeatureEnabled(tool: NavToolMenuItem): boolean {
  if (tool.requiresOntologyGraph && !isOntologyGraphFeatureVisible) return false;
  if (tool.requiresGsea && !isGseaEnabled) return false;
  if (tool.requiresGeoBrowse && !isGeoBrowseEnabled) return false;
  return true;
}

export function getFeaturedTool(tools: NavToolMenuItem[]): NavToolMenuItem | null {
  const eligible = tools.filter(tool => tool.canFeatureInProduction !== false);
  if (eligible.length === 0) return null;
  return eligible.reduce((latest, tool) =>
    Date.parse(tool.releasedAt) > Date.parse(latest.releasedAt) ? tool : latest,
  );
}

export function getToolGroupLabel(groupId: ToolGroupId): string {
  return TOOL_GROUPS.find(g => g.id === groupId)?.label ?? groupId;
}

const statisticsSections = sections.filter(s => s.group === 'Statistics');
const dataSections = sections.filter(s => s.group === 'Data');

const statisticsSubtitles: Record<string, string> = {
  gdaStatistics: 'Platform-wide metrics',
  stats_by_source: 'Breakdown per data source',
};

const dataSubtitles: Record<string, string> = {
  schema: 'Database structure',
  sources: 'Data origins',
  metrics: 'Scoring methodology',
  attributes: 'Field definitions',
  assoctypeont: 'Relationship types',
};

const dataIcons: Record<string, DocsIconKey> = {
  schema: 'database',
  sources: 'database',
  metrics: 'metrics',
  attributes: 'document',
  assoctypeont: 'ontology',
};

export const DOCS_MEGA_COLUMNS: DocsMenuColumn[] = [
  {
    title: 'DATA',
    items: dataSections.map(s => ({
      label: s.value === 'assoctypeont' ? 'Association Ontology' : s.label,
      subtitle: dataSubtitles[s.value] ?? 'Documentation',
      href: `/docs?section=${s.value}`,
      icon: dataIcons[s.value] ?? 'database',
    })),
  },
  {
    title: 'INFO',
    items: [
      {
        label: 'How to cite us',
        subtitle: 'Citation guidelines',
        href: '/docs?section=cite_us',
        icon: 'book',
      },
      {
        label: 'Releases',
        subtitle: 'Version history & changelog',
        href: '/docs?version=latest',
        icon: 'rocket',
      },
    ],
  },
  {
    title: 'STATISTICS',
    items: statisticsSections.map(s => ({
      label: s.label,
      subtitle: statisticsSubtitles[s.value] ?? 'Platform metrics',
      href: `/docs?section=${s.value}`,
      icon: 'chart' as DocsIconKey,
    })),
  },
];

export const DOCS_MEGA_MENU = {
  leftTitle: 'INFO',
  statisticsTitle: 'STATISTICS',
  statisticsItems: DOCS_MEGA_COLUMNS[1].items,
  dataTitle: 'DATA',
  dataItems: DOCS_MEGA_COLUMNS[2].items,
  rightItems: DOCS_MEGA_COLUMNS[0].items,
  overviewHref: '/docs',
};

export const DOCS_OVERVIEW_HREF = '/docs';

export const NAV_TOOLS_MENU: NavToolMenuItem[] = [
  {
    id: 'ai',
    label: 'AIDA',
    description: 'AI-driven discovery across genes, diseases, variants and more',
    infoHref: '/Tools?section=assistant',
    toolHref: '/Assistant',
    group: 'analysis',
    tag: 'AI assistant',
    releasedAt: '2026-05-19',
    iconSrc: '/static/images/eyesAida.svg',
    iconAlt: 'AIDA',
    feature: {
      eyebrow: 'Meet your research companion',
      title: 'A new perspective. With AIDA.',
      body: 'From complex questions to meaningful insights. Discover the power of AI-driven genomic research.',
      ctaLabel: 'Explore AIDA',
    },
  },
  {
    id: 'advanced-search',
    label: 'Advanced Search',
    description: 'Build multi-entity queries across the DISGENET knowledge graph',
    infoHref: '/advancedSearch',
    toolHref: '/advancedSearch',
    group: 'analysis',
    tag: 'Query builder',
    releasedAt: '2024-06-01',
    showInfo: false,
  },
  {
    id: 'geo-browse',
    label: 'ExpressNET',
    description: 'Explore gene expression patterns across diseases',
    infoHref: '/geo-browse',
    toolHref: '/geo-browse',
    group: 'analysis',
    tag: 'Gene expression',
    releasedAt: '2026-09-15',
    showInfo: false,
    requiresGeoBrowse: true,
    canFeatureInProduction: false,
    isNew: true,
    feature: {
      eyebrow: 'New analysis tool',
      title: 'Gene expression across diseases. With ExpressNET.',
      body: 'Browse expression studies and connect transcriptomic signals to DISGENET disease biology.',
      ctaLabel: 'Explore ExpressNET',
    },
  },
  {
    id: 'enrichment',
    label: 'Enrichment',
    description: 'Run functional enrichment on your gene sets',
    infoHref: '/Tools?section=enrichment',
    toolHref: '/enrichment',
    group: 'analysis',
    tag: 'Functional analysis',
    releasedAt: '2025-03-01',
  },
  {
    id: 'gsea-ranked',
    label: 'GSEA',
    description: 'Ranked gene set enrichment analysis',
    infoHref: '/Tools?section=gsea',
    toolHref: '/gsea-ranked',
    group: 'analysis',
    tag: 'Ranked enrichment',
    releasedAt: '2026-08-01',
    requiresGsea: true,
    canFeatureInProduction: false,
    isPaidTool: true,
    isNew: true,
    feature: {
      eyebrow: 'New enrichment workflow',
      title: 'Ranked enrichment. With GSEA.',
      body: 'Run gene set enrichment on ranked lists and surface biological signals grounded in DISGENET.',
      ctaLabel: 'Explore GSEA',
    },
  },
  {
    id: 'ontology-explorer',
    label: 'Ontology Explorer',
    description: 'Navigate disease hierarchies and ontology relations',
    infoHref: '/ontology-graph',
    toolHref: '/ontology-graph',
    group: 'analysis',
    tag: 'Disease hierarchy',
    releasedAt: '2026-07-01',
    showInfo: false,
    requiresOntologyGraph: true,
    canFeatureInProduction: false,
  },
  {
    id: 'api',
    label: 'REST API',
    description: 'Programmatic access to curated gene–disease data',
    infoHref: '/Tools?section=console',
    toolHref: '/interactive-console',
    group: 'access',
    tag: 'Programmatic',
    releasedAt: '2023-01-01',
    iconSrc: '/static/images/icons/api-icon.svg',
    iconAlt: 'REST API',
  },
  {
    id: 'download',
    label: 'Download',
    description: 'Export the full DISGENET database',
    infoHref: '/Tools?section=download',
    toolHref: '/Tools?section=download',
    group: 'access',
    tag: 'Data export',
    releasedAt: '2023-06-01',
    showInfo: false,
    roles: ['ROLE_UNLIMITED', 'ROLE_DEVELOPER'],
  },
  {
    id: 'mcp',
    label: 'MCP Connector',
    description: 'Make DISGENET data native to AI agents',
    infoHref: '/Tools?section=mcp',
    toolHref: '/Tools?section=mcp',
    group: 'access',
    tag: 'AI agents',
    releasedAt: '2026-09-01',
    showInfo: true,
    showTool: false,
    isNew: true,
    iconSrc: '/static/images/icons/iconsPink/062.svg',
    iconWidth: 80,
    iconAlt: 'MCP Connector',
    feature: {
      eyebrow: 'New way to connect',
      title: 'DISGENET data. Native to AI agents.',
      body: 'Connect DISGENET through MCP so your agents can query curated genotype–phenotype evidence directly.',
      ctaLabel: 'Explore MCP',
    },
  },
  {
    id: 'r-package',
    label: 'R Package',
    description: 'Query DISGENET from R with disgenet2r',
    infoHref: '/Tools?section=Rinstallation',
    toolHref: 'https://gitlab.com/medbio/disgenet2r',
    group: 'integrations',
    tag: 'R integration',
    releasedAt: '2022-01-01',
    toolExternal: true,
    iconSrc: '/static/images/icons/rPackage.svg',
    iconAlt: 'R Package',
  },
  {
    id: 'cytoscape',
    label: 'Cytoscape',
    description: 'Visualize gene–disease networks in Cytoscape',
    infoHref: '/Tools?section=installcyto',
    toolHref: 'https://apps.cytoscape.org/apps/disgenetapp',
    group: 'integrations',
    tag: 'Networks',
    releasedAt: '2022-06-01',
    toolExternal: true,
    iconSrc: '/static/images/icons/iconsPink/Cytoscape_logo.svg',
    iconAlt: 'Cytoscape',
  },
];

export const PRODUCT_DEMO_HREF = '/product-demo';
export const TOOLS_OVERVIEW_HREF = '/Tools';

export type InsightsMenuItem = {
  id: string;
  label: string;
  subtitle: string;
  href: string;
  icon: InsightsIconKey;
  external?: boolean;
};

export const INSIGHTS_MENU_ITEMS: InsightsMenuItem[] = [
  {
    id: 'case-studies',
    label: 'Case Studies',
    subtitle: 'Real-world applications',
    href: 'https://casestudies.disgenet.com',
    icon: 'caseStudies',
    external: true,
  },
  {
    id: 'publications',
    label: 'Publications',
    subtitle: 'Peer-reviewed research',
    href: '/publications',
    icon: 'publications',
  },
  ...(isEventsEnabled
    ? [
        {
          id: 'events',
          label: 'Events',
          subtitle: 'Webinars and conferences',
          href: '/events',
          icon: 'events' as InsightsIconKey,
        },
      ]
    : []),
  {
    id: 'blog',
    label: 'Blog',
    subtitle: 'News and articles',
    href: 'https://blog.disgenet.com/',
    icon: 'blog',
    external: true,
  },
];
